/**
 * The structured stem of an exam-prep item (#440, #362): a quantified claim over the kernel's expression language, as
 * JSON-safe data. The kernel's own `Expr` carries `bigint` (which JSON cannot), so a stem mirrors it with decimal strings; the
 * conversion both ways is here, and nothing else in the prep layer touches the kernel's constant representation.
 *
 * Only exact integers, variables, `+ - * /` and integer powers are representable. A decimal or the imaginary unit is
 * REFUSED (`undefined`), never rounded: an item DaVinci cannot state exactly is not an item. No float exists in this file.
 *
 * Pure: imports only from this package.
 */

import type { C, Constraint, DomainMap, Expr, Relation, VarDomain } from '../certificates/index.js';
import { cFromInt, qIsInt, qIsZero } from '../certificates/index.js';

export type StemExpr =
  /** A non-negative integer literal, as a decimal string. A negative number is `neg` of one. */
  | { readonly k: 'int'; readonly v: string }
  | { readonly k: 'var'; readonly name: string }
  | { readonly k: 'neg'; readonly a: StemExpr }
  | { readonly k: 'add' | 'sub' | 'mul' | 'div'; readonly a: StemExpr; readonly b: StemExpr }
  /** The exponent is an integer literal, as a decimal string, and is never varied by the generator. */
  | { readonly k: 'pow'; readonly base: StemExpr; readonly exp: string };

export interface StemConstraint {
  readonly lhs: StemExpr;
  readonly rel: Relation;
  readonly rhs: StemExpr;
}

/** Where the variables range. `N0` / `N1` are the natural numbers from 0 / from 1, as a course's sourced rule fixes them. */
export type StemDomain = 'N0' | 'N1' | 'Z' | 'Q';

export const PREP_TASK_KINDS = ['witness', 'counterexample'] as const;
export type PrepTaskKind = (typeof PREP_TASK_KINDS)[number];

/**
 * What the learner is asked to do.
 *
 *   witness          "find values of the variables that make every `claim` constraint true"
 *   counterexample   "the claim `given` implies `claim`, for all values; find values that satisfy every `given` constraint and
 *                     make some `claim` constraint false"
 */
export interface PrepStem {
  readonly taskKind: PrepTaskKind;
  readonly domain: StemDomain;
  /** Sorted. Every one is assigned by a correct answer. */
  readonly variables: readonly string[];
  /** Hypotheses. Empty for a witness. */
  readonly given: readonly StemConstraint[];
  /** Witness: the constraints to satisfy. Counterexample: the conclusions to falsify. At least one. */
  readonly claim: readonly StemConstraint[];
}

// -------------------------------------------------------------- kernel -> stem

export function stemExprOfKernel(e: Expr): StemExpr | undefined {
  switch (e.k) {
    case 'const': {
      if (e.form !== 'integer' || !qIsZero(e.v.im) || !qIsInt(e.v.re) || e.v.re.n < 0n) return undefined;
      return { k: 'int', v: e.v.re.n.toString() };
    }
    case 'var':
      return { k: 'var', name: e.name };
    case 'neg': {
      const a = stemExprOfKernel(e.a);
      return a === undefined ? undefined : { k: 'neg', a };
    }
    case 'pow': {
      const base = stemExprOfKernel(e.base);
      return base === undefined ? undefined : { k: 'pow', base, exp: e.exp.toString() };
    }
    default: {
      const a = stemExprOfKernel(e.a);
      const b = stemExprOfKernel(e.b);
      return a === undefined || b === undefined ? undefined : { k: e.k, a, b };
    }
  }
}

export function stemConstraintOfKernel(c: Constraint): StemConstraint | undefined {
  const lhs = stemExprOfKernel(c.lhs);
  const rhs = stemExprOfKernel(c.rhs);
  return lhs === undefined || rhs === undefined ? undefined : { lhs, rel: c.rel, rhs };
}

// -------------------------------------------------------------- stem -> kernel

export function kernelExprOfStem(e: StemExpr): Expr {
  switch (e.k) {
    case 'int':
      return { k: 'const', v: cFromInt(BigInt(e.v)), form: 'integer' };
    case 'var':
      return { k: 'var', name: e.name };
    case 'neg':
      return { k: 'neg', a: kernelExprOfStem(e.a) };
    case 'pow':
      return { k: 'pow', base: kernelExprOfStem(e.base), exp: BigInt(e.exp) };
    default:
      return { k: e.k, a: kernelExprOfStem(e.a), b: kernelExprOfStem(e.b) };
  }
}

export const kernelConstraintOfStem = (c: StemConstraint): Constraint => ({ lhs: kernelExprOfStem(c.lhs), rel: c.rel, rhs: kernelExprOfStem(c.rhs) });

export function kernelDomainOfStem(domain: StemDomain): VarDomain {
  switch (domain) {
    case 'N0':
      return { kind: 'N', start: 0n };
    case 'N1':
      return { kind: 'N', start: 1n };
    case 'Z':
      return { kind: 'Z' };
    case 'Q':
      return { kind: 'Q' };
  }
}

/** The stem as the kernel's inputs. */
export function kernelOfStem(stem: PrepStem): {
  readonly given: readonly Constraint[];
  readonly claim: readonly Constraint[];
  readonly domains: DomainMap;
} {
  const domain = kernelDomainOfStem(stem.domain);
  return {
    given: stem.given.map(kernelConstraintOfStem),
    claim: stem.claim.map(kernelConstraintOfStem),
    domains: Object.fromEntries(stem.variables.map((name) => [name, domain])),
  };
}

/** The single domain every variable of a map shares, or undefined (mixed, empty or unknown). */
export function stemDomainOfKernel(domains: DomainMap): StemDomain | undefined {
  const all = Object.values(domains);
  const first = all[0];
  if (first === undefined) return undefined;
  const label = (d: VarDomain): StemDomain | undefined => {
    if (d.kind === 'N') return d.start === 0n ? 'N0' : d.start === 1n ? 'N1' : undefined;
    return d.kind === 'Z' || d.kind === 'Q' ? d.kind : undefined;
  };
  const wanted = label(first);
  if (wanted === undefined) return undefined;
  return all.every((d) => label(d) === wanted) ? wanted : undefined;
}

/** Structural equality of two stem expressions (the generator's "did anything change"). */
export function stemExprEqual(a: StemExpr, b: StemExpr): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** A kernel complex number as the exact text a learner writes (`3`, `-1`, `1/2`); undefined when it is not real. */
export function exactNumberText(value: C): string | undefined {
  if (!qIsZero(value.im)) return undefined;
  return value.re.d === 1n ? value.re.n.toString() : `${value.re.n.toString()}/${value.re.d.toString()}`;
}
